// GameSession: one long-lived object that runs a match at a time. It owns the connection, the
// prediction, input, FX glue and the frame loop, and drives `gameStatus`.

import { create } from "@bufbuild/protobuf";
import { errorMessage } from "../api/client";
import { RoomKind } from "../api/types";
import type { AudioEngine } from "../audio";
import type { Hud } from "../hud";
import { connectGame, LEAD_SEED_PEAK_TICKS, SnapshotBuffer, TickClock, TICK_DT } from "../net";
import { TICK_RATE } from "../net/constants";
import type { GameConnection, InterpolatedPlayer } from "../net";
import {
  ChatSendSchema,
  ClientMessageSchema,
  CycleSpectateSchema,
  ReadySchema,
  SelectLoadoutSchema,
} from "../proto/gunrun/v1/game_pb";
import type { ClientMessage, ServerMessage, Spawned, Welcome } from "../proto/gunrun/v1/game_pb";
import type { MapFileJson, Renderer } from "../render";
import { frameCap, isDesktopShell } from "../settings/frameLimiter";
import { bindingLabel } from "../settings/keybinds";
import { settings } from "../settings/store";
import { gameError, gameLoadProgress, gameStatus, leaveGame } from "../state/game";
import type { GameJoin } from "../state/game";
import { toast } from "../state/toasts";
import { LeadControl, WorldHandle, initWasm, weaponStatsJson } from "../wasm";
import { PlayerFlags, SurfaceFlags } from "../wasm/types";
import type { FxEventData, ProjectileSnapData, SnapshotData, Vec3Like } from "../wasm/types";
import { clamp } from "./angles";
import type { CameraPose } from "./camera";
import { CountdownCue, ProjectileAudio, viewForward } from "./audioCues";
import type { ProjectileCue } from "./audioCues";
import { FxDirector } from "./fx";
import { GlassController } from "./glass";
import { ExplosionDedupe } from "./fxDedupe";
import { Sec, perfBegin, perfEnd, perfMark, probe } from "../perf/probe";
import { InputController } from "./input";
import { FrameLoop, layerNudge } from "./loop";
import { FollowCam } from "./followCam";
import type { FollowTarget, RayDist } from "./followCam";
import { LocalPlayer } from "./local";
import { MessageRouter } from "./messages";
import { ObjectiveTracker } from "./objectives";
import { ObjectiveAudio } from "./objectiveAudio";
import type { ObjectiveContext } from "./objectives";
import { ProjectileTimeline } from "./projectileTimeline";
import { LobbyModel, lobbyView, registerLobbyActions } from "./lobby";
import {
  addBotMessage,
  kickMessage,
  removeBotMessage,
  selectTeamMessage,
  setMapMessage,
  setModeMessage,
  setRulesMessage,
  startMatchMessage,
} from "./lobbyMessages";
import { gamePaused, matchActive, nextLoadout, pauseView, pointerLocked, registerPauseActions } from "./pause";
import { RemotePlayers } from "./remote";
import { RoomState } from "./room";
import { canDamage, isShootable, shotTargetOf } from "./shotTrace";
import type { ShotTarget } from "./shotTrace";
import { captureTrace } from "./trace";
import { RemoteStatsCache } from "./weaponStats";

type Stage = "idle" | "connecting" | "loading" | "awaitSpawn" | "playing";

interface QueuedEvent {
  tick: number;
  ev: FxEventData;
}

/** Cap on delayed remote effects so a stalled render clock cannot grow the queue without bound. */
const MAX_QUEUED_EVENTS = 256;

export class GameSession {
  private stage: Stage = "idle";
  /** The server announced the end of the match (a ranked room closes soon after its end screen). */
  private matchOver = false;
  private gen = 0;
  private conn: GameConnection | null = null;
  private world: WorldHandle | null = null;
  private paused = false;
  private lastFrame = 0;
  private spectatingId: number | null = null;
  /** An F8 trace upload is in flight. */
  private traceBusy = false;

  private readonly tickClock = new TickClock(() => new LeadControl(LEAD_SEED_PEAK_TICKS));
  private readonly snapshots = new SnapshotBuffer(settings.value.network.interpolation);
  private readonly samples = new Map<number, InterpolatedPlayer>();
  private readonly queue: QueuedEvent[] = [];
  private readonly explosions = new ExplosionDedupe();
  private readonly projectileTimeline = new ProjectileTimeline();
  private readonly projectileAudio = new ProjectileAudio();
  private readonly projectileCues: ProjectileCue[] = [];
  private readonly countdown = new CountdownCue();
  private readonly objectiveAudio: ObjectiveAudio;
  private readonly remoteStats = new RemoteStatsCache(weaponStatsJson);

  private readonly fx: FxDirector;
  private readonly remotes: RemotePlayers;
  private readonly room: RoomState;
  private readonly objectives: ObjectiveTracker;
  private readonly lobby = new LobbyModel();
  /** `LobbyModel.epoch` the lobby panel was last opened automatically for. */
  private lobbyShownEpoch = -1;
  private readonly input: InputController;
  private readonly local: LocalPlayer;
  private readonly glass: GlassController;
  private readonly router: MessageRouter;
  private readonly loop: FrameLoop;

  constructor(
    private readonly renderer: Renderer,
    private readonly hud: Hud,
    private readonly audio: AudioEngine,
    canvas: HTMLCanvasElement,
  ) {
    this.fx = new FxDirector(renderer, audio);
    this.fx.targets = (shooterId, atTick) => this.shotTargetsFor(shooterId, atTick);
    this.remotes = new RemotePlayers(renderer, audio, this.fx);
    this.room = new RoomState(hud, this.remotes);
    this.objectives = new ObjectiveTracker(renderer, hud);
    this.objectiveAudio = new ObjectiveAudio(audio);
    this.input = new InputController(canvas, {
      onPauseRequested: () => this.pause(),
      onChat: (teamOnly) => this.openChat(teamOnly),
      onScoreboard: (show) => hud.showScoreboard(show),
      onLobby: () => this.toggleLobby(),
      onTrace: () => this.saveTrace(),
      onFire: () => this.cycleSpectate(),
      chatIsOpen: () => hud.chatIsOpen(),
      onLockChange: (locked) => {
        pointerLocked.value = locked;
      },
    });
    this.local = new LocalPlayer({
      renderer,
      hud,
      audio,
      fx: this.fx,
      input: this.input,
      tickClock: this.tickClock,
      snapshots: this.snapshots,
      room: this.room,
    });
    probe?.expose("local", this.local);
    probe?.expose("session", this);
    if (probe) {
      const at = new Map<number, InterpolatedPlayer>();
      probe.hitreg.attach((tick, id) => {
        this.snapshots.samplePlayers(tick, at);
        const p = at.get(id);
        return p ? { alive: (p.flags & PlayerFlags.ALIVE) !== 0, target: shotTargetOf(p) } : null;
      }, this.fx.hitboxRay);
    }
    this.tickClock.onResync = () => this.local.onResync();
    this.glass = new GlassController({ renderer, audio, onWorldChanged: () => this.local.onWorldChanged() });
    this.fx.onLocalPane = (brush, at, dir) => this.glass.predictBreak(brush, at, dir, performance.now());
    this.router = new MessageRouter({
      hud,
      audio,
      renderer,
      room: this.room,
      remotes: this.remotes,
      fx: this.fx,
      local: this.local,
      input: this.input,
      objectives: this.objectives,
      lobby: this.lobby,
      glass: this.glass,
      onLobbyChanged: () => this.syncLobby(),
      serverTick: (now) => this.conn?.clock.estServerTick(now) ?? 0,
      onWelcome: (w) => void this.onWelcome(w),
      onSpawned: (s) => this.onSpawned(s),
      onKicked: (reason) => this.fail(reason ? `Kicked from the match: ${reason}` : "Kicked from the match."),
    });
    // Only the desktop app needs the nudge; in a vsynced browser it delays DOM updates by a frame.
    this.loop = new FrameLoop(this.frame, isDesktopShell() ? layerNudge() : undefined);
    registerPauseActions({ resume: () => this.resume(), selectLoadout: (i) => this.selectLoadout(i) });
    registerLobbyActions({
      startMatch: () => this.send(startMatchMessage()),
      setMap: (map) => this.send(setMapMessage(map)),
      setMode: (mode) => this.send(setModeMessage(mode)),
      setRules: (patch) => {
        const view = this.lobby.view();
        if (view) this.send(setRulesMessage(view.rules, patch));
      },
      kick: (id) => this.send(kickMessage(id)),
      selectTeam: (team) => this.send(selectTeamMessage(team)),
      addBot: (team, level) => this.send(addBotMessage(team, level)),
      removeBot: (id) => this.send(removeBotMessage(id)),
    });
  }

  // ---- Lifecycle ----

  /** Connects to a room. Replaces any running match. */
  async start(join: GameJoin): Promise<void> {
    this.teardown();
    const gen = ++this.gen;
    this.stage = "connecting";
    this.matchOver = false;
    gameError.value = "";
    gameStatus.value = "connecting";
    try {
      void this.audio.init();
      this.audio.unlock();
      await initWasm();
      if (gen !== this.gen) return;
      const conn = await connectGame(join, { transport: settings.value.network.transport });
      if (gen !== this.gen) {
        conn.close();
        return;
      }
      this.conn = conn;
      this.tickClock.newConnection();
      conn.onClose = (reason) => {
        if (gen !== this.gen) return;
        // A ranked room plays one match and closes after its end screen: that is the way out, not
        // a lost connection.
        if (this.matchOver && join.room.kind === RoomKind.RANKED) {
          leaveGame();
          return;
        }
        this.fail(reason ? `Connection lost: ${reason}` : "Connection lost.");
      };
      conn.onSnapshot = (s, arrivalMs) => {
        if (gen === this.gen) this.onSnapshot(s, arrivalMs);
      };
      // Assigned last: the connection delivers everything it queued so far.
      conn.onMessage = (m) => {
        if (gen === this.gen) this.onMessage(m);
      };
    } catch (e) {
      if (gen === this.gen) this.fail(errorMessage(e));
    }
  }

  /** Leaves the match: stops everything and releases the connection. */
  leave(): void {
    if (this.conn) {
      try {
        this.conn.send(create(ClientMessageSchema, { msg: { case: "leave", value: {} } }));
      } catch {
        // Already closing.
      }
    }
    this.teardown();
  }

  /** Stops the match with an error on the connection panel (the join stays for "Retry"). */
  private fail(message: string): void {
    if (this.stage === "idle") return;
    this.teardown();
    gameError.value = message;
    gameStatus.value = "error";
  }

  private teardown(): void {
    this.gen++;
    this.loop.stop();
    this.input.detach();
    const conn = this.conn;
    this.conn = null;
    if (conn) {
      conn.onClose = () => undefined;
      conn.onMessage = () => undefined;
      conn.onSnapshot = () => undefined;
      conn.close();
    }
    this.local.detach();
    this.world?.free();
    this.world = null;
    this.fx.world = null;
    this.fx.clear();
    this.glass.clear();
    this.remotes.clear();
    this.objectives.clear();
    this.objectiveAudio.clear();
    this.projectileAudio.clear();
    this.countdown.reset();
    this.queue.length = 0;
    this.explosions.clear();
    this.projectileTimeline.clear();
    this.samples.clear();
    this.snapshots.clear();
    this.spectatingId = null;
    this.room.me.alive = false;
    this.audio.setLowHealth(false);
    this.audio.stopWind();
    this.audio.setOcclusionFn(null);
    this.hud.setVisible(false);
    this.hud.matchEnd(null);
    this.hud.death(null);
    this.hud.spectating(null);
    this.hud.scope(false);
    this.hud.showScoreboard(false);
    this.hud.netGraph(null);
    this.stage = "idle";
    this.paused = false;
    gamePaused.value = false;
    pauseView.value = "menu";
    pointerLocked.value = false;
    matchActive.value = false;
    nextLoadout.value = null;
    this.lobby.clear();
    this.lobbyShownEpoch = -1;
    lobbyView.value = null;
    this.hud.lobbyHint(false, false, "");
  }

  // ---- Messages ----

  private onMessage(msg: ServerMessage): void {
    try {
      if (msg.msg.case === "matchEnded") this.matchOver = true;
      this.router.handle(msg, performance.now());
    } catch (e) {
      console.error("game message failed", msg.msg.case, e);
    }
  }

  /** Welcome (first one, or the next map of a cycling room): load the world, then announce Ready. */
  private async onWelcome(w: Welcome): Promise<void> {
    perfMark("welcome");
    const gen = this.gen;
    const conn = this.conn;
    if (!conn) return;
    // A new map (also mid-match): the gate takes over the screen, input and the loop stop until the next spawn.
    this.loop.stop();
    this.input.detach();
    this.paused = false;
    gamePaused.value = false;
    pointerLocked.value = false;
    matchActive.value = false;
    this.stage = "loading";
    gameStatus.value = "loading";
    gameLoadProgress.value = 0;
    this.local.alive = false;
    this.audio.setLowHealth(false);
    this.audio.stopWind();
    this.room.applyWelcome(w);
    this.glass.reset(w.brokenGlass);
    this.lobby.reset(w);
    this.syncLobby();
    this.remotes.clear();
    for (const p of w.players) this.remotes.upsert(p);
    this.objectives.clear();
    this.objectiveAudio.clear();
    this.projectileAudio.clear();
    this.countdown.reset();
    this.queue.length = 0;
    this.explosions.clear();
    this.projectileTimeline.clear();
    this.snapshots.clear();
    this.hud.matchEnd(null);
    this.hud.death(null);
    try {
      const res = await fetch(`/maps/${encodeURIComponent(w.map)}.json`, { cache: "no-cache" });
      if (!res.ok) throw new Error(`Map "${w.map}" could not be downloaded (HTTP ${res.status}).`);
      const text = await res.text();
      perfMark("map-downloaded");
      if (gen !== this.gen) return;
      const world = WorldHandle.fromJson(text);
      if (world.mapHash() !== w.mapHash.toLowerCase()) {
        world.free();
        throw new Error("The map on the server differs from the one in your browser cache. Reload the page and try again.");
      }
      const map = JSON.parse(text) as MapFileJson;
      this.local.detach();
      this.world?.free();
      this.world = world;
      this.fx.world = world;
      this.fx.surfaces.load(map);
      perfMark("world-built");
      const loaded = await this.renderer.loadMap(map, world.renderMesh(), {
        onProgress: (fraction) => {
          gameLoadProgress.value = fraction;
        },
        cancelled: () => gen !== this.gen,
      });
      if (!loaded || gen !== this.gen) return;
      this.glass.attach(world);
      this.objectives.load(map, this.room.mode);
      this.local.attach(world, w.moveConfigJson);
      this.local.trace.setSession(w.map, w.roomId, w.yourId, conn.transportKind);
      this.audio.setOcclusionFn((from, to) => this.occlusion(from, to));
      this.stage = "awaitSpawn";
      conn.send(create(ClientMessageSchema, { msg: { case: "ready", value: create(ReadySchema) } }));
      perfMark("ready-sent");
    } catch (e) {
      if (gen === this.gen) this.fail(errorMessage(e));
    }
  }

  private onSpawned(s: Spawned): void {
    const conn = this.conn;
    if (!conn || this.stage === "loading" || this.stage === "connecting") return;
    this.local.spawn(s, performance.now(), conn);
    perfMark("spawned");
    this.spectatingId = null;
    this.enterPlaying();
  }

  private enterPlaying(): void {
    if (this.stage === "playing") return;
    this.stage = "playing";
    gameStatus.value = "playing";
    matchActive.value = true;
    this.hud.applySettings(settings.value);
    this.hud.setVisible(true);
    this.hud.netGraph(this.conn?.stats ?? null);
    this.input.attach();
    this.input.enabled = !this.paused;
    this.lastFrame = performance.now();
    this.loop.start(frameCap(settings.value.video.frameLimiter));
    this.renderer.resize();
    this.syncLobby();
  }

  // ---- Snapshots ----

  private onSnapshot(s: SnapshotData, arrivalMs: number): void {
    const conn = this.conn;
    if (!conn) return;
    probe?.recordSnapshot(arrivalMs, s.tick);
    this.snapshots.push(s, arrivalMs);
    probe?.hitreg.expire(s.tick);
    this.spectatingId = s.spectating;
    if (this.stage === "awaitSpawn" && s.spectating !== null && s.local === null) {
      this.enterPlaying();
    }
    if (this.stage !== "playing") return;
    this.local.onSnapshot(s, arrivalMs, conn);
    conn.stats.interpMs = this.snapshots.interpDelayMs();
    this.projectileTimeline.push(s.tick, s.projectiles);
    if (s.events.length > 0) {
      for (const ev of s.events) {
        // Explosions are resent until the server knows one arrived; a copy of an event taken already is skipped.
        if (ev.kind === "explosion" && !this.explosions.isNew(s.tick, ev)) continue;
        if (this.queue.length >= MAX_QUEUED_EVENTS) this.queue.shift();
        this.queue.push({ tick: s.tick, ev });
      }
    }
    const room = this.room;
    const m = s.mode;
    room.phase = m.phase;
    room.phaseEndTick = m.phaseEndTick;
    room.scores = m.scores;
    room.round = room.mode === "defusal" ? m.round : null;
    this.objectives.onSnapshot(m);
    this.objectiveAudio.onSnapshot(m);
    room.pushMatch(s.tick, arrivalMs);
  }

  // ---- Frame ----

  private readonly frame = (nowMs: number): void => {
    const conn = this.conn;
    if (this.stage !== "playing" || !conn) return;
    probe?.frameStart(nowMs);
    const dt = clamp((nowMs - this.lastFrame) / 1000, 0.0005, 0.1);
    this.lastFrame = nowMs;
    // An error in the game logic must not freeze the picture while the connection stays up and
    // nobody sees why: the frame is always drawn, then the match ends with the error panel.
    let failure: unknown;
    let failed = false;
    perfBegin(Sec.update);
    try {
      this.update(nowMs, dt, conn);
    } catch (e) {
      failed = true;
      failure = e;
    }
    perfEnd(Sec.update);
    perfBegin(Sec.render);
    try {
      this.renderer.render(dt);
    } catch (e) {
      if (!failed) failure = e;
      failed = true;
    }
    perfEnd(Sec.render);
    probe?.frameEnd();
    if (failed) {
      console.error("frame failed", failure);
      this.fail(`Game error: ${errorMessage(failure)}`);
    }
  };

  /** Input, prediction, remote players, effects, audio and HUD of one frame (everything but drawing). */
  private update(nowMs: number, dt: number, conn: GameConnection): void {
    const local = this.local;
    const input = this.input;
    input.enabled = !this.paused;

    perfBegin(Sec.snap);
    const renderTick = this.snapshots.renderTick(nowMs);
    // Sampled first: a shot fired this frame ends in the bodies as they are displayed now.
    this.snapshots.samplePlayers(renderTick, this.samples);
    const projectiles = this.projectileTimeline.take(renderTick);
    if (projectiles !== null) {
      this.renderer.projectiles.sync(projectiles.list, (renderTick - projectiles.tick) * TICK_DT);
      this.projectileSounds(projectiles.list, projectiles.tick);
    }
    perfEnd(Sec.snap);
    perfBegin(Sec.local);
    if (local.alive) local.step(nowMs, dt, renderTick, conn);
    else input.discardMouse();
    this.glass.frame(nowMs);
    perfEnd(Sec.local);

    perfBegin(Sec.camera);
    // The death sink plays out before the camera follows the killer (or a teammate in Defusal).
    const target =
      local.alive || this.spectatingId === null || !local.camera.deathSettled() ? undefined : this.samples.get(this.spectatingId);
    const pose = this.cameraPose(dt, nowMs, target);
    this.renderer.setCamera(pose.pos, pose.yaw, pose.pitch, pose.roll, pose.hFovDeg);
    this.updateListener(pose);
    perfEnd(Sec.camera);

    perfBegin(Sec.remotes);
    this.remotes.sync(this.samples, nowMs, dt, pose.pos, target !== undefined && this.followCam.hideTarget ? target.id : null);
    perfEnd(Sec.remotes);
    perfBegin(Sec.events);
    this.processEvents(renderTick, nowMs, pose);
    perfEnd(Sec.events);

    perfBegin(Sec.viewmodel);
    local.updateViewmodel(nowMs, dt, target === undefined);
    perfEnd(Sec.viewmodel);
    perfBegin(Sec.hud);
    this.hud.spectating(target ? (this.remotes.name(target.id) ?? null) : null, target && this.spectatesTeam() ? "FIRE: NEXT PLAYER" : null);
    perfEnd(Sec.hud);
    perfBegin(Sec.audio);
    this.updateAudio(nowMs);
    perfEnd(Sec.audio);
    perfBegin(Sec.objectives);
    this.updateObjectives(nowMs, pose);
    perfEnd(Sec.objectives);
    perfBegin(Sec.hud);
    local.updateHud(nowMs, conn, pose.hFovDeg, target !== undefined);
    perfEnd(Sec.hud);
  }

  private readonly shotTargets: ShotTarget[] = [];
  /** Remote players at the moment of a local shot (`shotTargetsFor` with a time). */
  private readonly shotSamples = new Map<number, InterpolatedPlayer>();

  /**
   * The displayed players a bullet of `shooterId` can hit: alive, not spawn protected, damageable by
   * the shooter's team. As shown this frame, or at remote time `atTick` (a local shot: the moment it
   * was fired, which the server's lag compensation rewinds to).
   */
  private shotTargetsFor(shooterId: number, atTick?: number): readonly ShotTarget[] {
    const out = this.shotTargets;
    out.length = 0;
    const room = this.room;
    const shooterTeam = shooterId === room.myId ? room.myTeam : this.remotes.team(shooterId);
    const friendlyFire = room.rules?.friendlyFire ?? false;
    let players = this.samples;
    if (atTick !== undefined) {
      players = this.shotSamples;
      this.snapshots.samplePlayers(atTick, players);
    }
    for (const p of players.values()) {
      if (p.id === shooterId || !isShootable(p.flags)) continue;
      if (!canDamage(shooterTeam, this.remotes.team(p.id), friendlyFire)) continue;
      out.push(shotTargetOf(p));
    }
    return out;
  }

  private cameraPose(dt: number, nowMs: number, target: InterpolatedPlayer | undefined): CameraPose {
    const local = this.local;
    if (local.alive && local.view) {
      this.followCam.reset();
      return local.pose(dt, nowMs);
    }
    if (target) {
      const f = this.followTarget;
      f.id = target.id;
      f.pos = target.pos;
      f.yaw = target.yaw;
      f.pitch = target.pitch;
      f.crouched = (target.flags & PlayerFlags.CROUCHED) !== 0;
      f.sliding = (target.flags & PlayerFlags.SLIDING) !== 0;
      return this.followCam.update(dt, f, this.followRay, settings.value.video.fov);
    }
    // The watched player died or left: the view stays where the follow camera left it.
    if (this.followCam.active) return this.followCam.pose;
    return local.camera.updateDead(dt);
  }

  private readonly followCam = new FollowCam();
  private readonly followTarget: FollowTarget = { id: -1, pos: { x: 0, y: 0, z: 0 }, yaw: 0, pitch: 0, crouched: false, sliding: false };
  private readonly followOrigin = { x: 0, y: 0, z: 0 };
  private readonly followRay: RayDist = (ox, oy, oz, dx, dy, dz, maxDist) => {
    const o = this.followOrigin;
    o.x = ox;
    o.y = oy;
    o.z = oz;
    return this.fx.ray(o, dx, dy, dz, maxDist)?.dist;
  };

  private readonly fwd = { x: 0, y: 0, z: -1 };
  private readonly up = { x: 0, y: 1, z: 0 };

  private updateListener(pose: CameraPose): void {
    this.audio.setListener(pose.pos, viewForward(pose.yaw, pose.pitch, this.fwd), this.up);
  }

  private updateAudio(nowMs: number): void {
    const local = this.local;
    this.audio.setWind(local.alive && local.view ? local.view.speed : 0);
    this.audio.setLowHealth(local.lowHealth());
    const tick = this.conn?.clock.estServerTick(nowMs) ?? 0;
    if (this.countdown.poll(this.room.phase, this.room.phaseEndTick, tick, TICK_RATE)) this.audio.playUi("countdown");
  }

  /** Throw and bounce sounds of the thrown equipment in the snapshot just shown. */
  private projectileSounds(list: readonly ProjectileSnapData[], tick: number): void {
    const cues = this.projectileCues;
    cues.length = 0;
    this.projectileAudio.update(list, tick, this.room.myId, cues);
    for (const c of cues) this.audio.playGrenade(c.cue, c.pos);
  }

  private processEvents(renderTick: number, nowMs: number, pose: CameraPose): void {
    const q = this.queue;
    while (q.length > 0 && (q[0] as QueuedEvent).tick <= renderTick) {
      const ev = (q.shift() as QueuedEvent).ev;
      switch (ev.kind) {
        case "shot": {
          if (ev.shooter === this.room.myId) break;
          this.remotes.markFiring(ev.shooter, nowMs);
          const attachments = this.remotes.attachmentsOf(ev.shooter, ev.weapon);
          this.fx.remoteShot(ev, attachments, this.remoteStats.get(ev.weapon, attachments).hideFlash);
          break;
        }
        case "melee": {
          // The own swing already played from the prediction; the echo only adds the verdict of a hit.
          if (ev.attacker === this.room.myId && !ev.hit) break;
          this.remotes.markFiring(ev.attacker, nowMs);
          const who = this.samples.get(ev.attacker);
          this.audio.playReload(ev.hit ? "knifeHit" : "knifeSwing", who?.pos);
          break;
        }
        case "explosion": {
          const cp = Math.cos(pose.pitch);
          this.fx.explosion(ev, pose.pos, -Math.sin(pose.yaw) * cp, Math.sin(pose.pitch), -Math.cos(pose.yaw) * cp);
          break;
        }
      }
    }
  }

  private readonly objCtx: ObjectiveContext = {
    mode: "ffa",
    myId: -1,
    myTeam: "none",
    feet: null,
    eye: { x: 0, y: 0, z: 0 },
    yaw: 0,
    nowTick: 0,
    frozen: false,
    interactBinding: "",
  };
  private readonly nameOf = (id: number): string | null => this.remotes.name(id);
  private readonly positionOf = (id: number): Vec3Like | null => this.samples.get(id)?.pos ?? null;

  private updateObjectives(nowMs: number, pose: CameraPose): void {
    const conn = this.conn;
    if (!conn) return;
    const local = this.local;
    const c = this.objCtx;
    c.mode = this.room.mode;
    c.myId = this.room.myId;
    c.myTeam = this.room.myTeam;
    c.feet = local.alive && local.view ? local.view.pos : null;
    c.eye = pose.pos;
    c.yaw = pose.yaw;
    c.nowTick = conn.clock.estServerTick(nowMs);
    c.frozen = this.room.phase === "round_freeze";
    c.interactBinding = settings.value.keybinds.interact[0] ?? "";
    this.objectives.updateHud(c, this.nameOf);
    this.objectiveAudio.frame(nowMs, c.nowTick, c.myId, this.positionOf);
  }

  /** 0..1 audio occlusion between a sound and the listener: walls block, glass only dampens. */
  private occlusion(from: Vec3Like, to: Vec3Like): number {
    const world = this.world;
    if (!world) return 0;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1) return 0;
    const hit = world.raycast(from.x, from.y, from.z, dx / dist, dy / dist, dz / dist, dist);
    if (!hit || hit.dist > dist - 0.3) return 0;
    return (hit.surface & SurfaceFlags.GLASS) !== 0 ? 0.3 : 0.85;
  }

  // ---- Spectating ----

  /** The dead watch their living teammates and may switch between them (Defusal). */
  private spectatesTeam(): boolean {
    return this.room.mode === "defusal";
  }

  /** Fire while dead: watch the next living teammate. */
  private cycleSpectate(): void {
    if (this.stage !== "playing" || this.paused || this.local.alive || !this.spectatesTeam()) return;
    this.conn?.send(create(ClientMessageSchema, { msg: { case: "cycleSpectate", value: create(CycleSpectateSchema) } }));
  }

  /** F8: uploads the movement trace of the last ten seconds to a `--dev` server and says how it went. */
  private saveTrace(): void {
    if (this.traceBusy) return;
    this.traceBusy = true;
    void captureTrace(this.local.trace)
      .then((n) => toast(n.message, n.kind, n.kind === "success" ? 8000 : 5000))
      .finally(() => {
        this.traceBusy = false;
      });
  }

  // ---- Pause, chat, loadout ----

  /** Opens the pause menu (Escape while unlocked, browser Back). */
  pause(): void {
    if (this.stage !== "playing" || this.paused) return;
    this.paused = true;
    this.input.enabled = false;
    this.input.releaseAll();
    this.input.releaseLock();
    pauseView.value = "menu";
    gamePaused.value = true;
  }

  private resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.input.enabled = true;
    gamePaused.value = false;
    pauseView.value = "menu";
    this.input.requestLock();
    this.syncLobby();
  }

  // ---- Custom lobby ----

  private send(msg: ClientMessage): void {
    this.conn?.send(msg);
  }

  /** The lobby panel is on screen (it is the pause overlay with the lobby view). */
  private lobbyOpen(): boolean {
    return this.paused && pauseView.value === "lobby";
  }

  private openLobby(): void {
    if (this.stage !== "playing" || !this.lobby.isCustom) return;
    if (!this.paused) this.pause();
    pauseView.value = "lobby";
    this.syncLobby();
  }

  /** The lobby key: opens the panel, or closes it again. */
  private toggleLobby(): void {
    if (this.lobbyOpen()) this.resume();
    else this.openLobby();
  }

  /**
   * Publishes the lobby for the panel and keeps what depends on it in step: the HUD hint while the lobby
   * waits, the panel opening by itself once per warmup, and closing when the match starts.
   */
  private syncLobby(): void {
    const view = this.lobby.view();
    lobbyView.value = view;
    const playing = this.stage === "playing";
    const waiting = view !== null && view.phase === "warmup";
    this.hud.lobbyHint(
      playing && waiting && !this.lobbyOpen(),
      view?.isHost ?? false,
      bindingLabel(settings.value.keybinds.lobby[0] ?? ""),
    );
    if (!playing || view === null) return;
    if (waiting && this.lobby.epoch !== this.lobbyShownEpoch) {
      this.lobbyShownEpoch = this.lobby.epoch;
      this.openLobby();
    } else if (!waiting && view.phase !== "ended" && this.lobbyOpen()) {
      this.resume();
    }
  }

  private openChat(teamOnly: boolean): void {
    const team = teamOnly && this.room.mode !== "ffa";
    this.hud.chatOpen(team, (text, only) => {
      const trimmed = text.trim().slice(0, 200);
      if (trimmed.length === 0) return;
      this.conn?.send(
        create(ClientMessageSchema, {
          msg: { case: "chat", value: create(ChatSendSchema, { teamOnly: only && this.room.mode !== "ffa", text: trimmed }) },
        }),
      );
    });
  }

  /** Applies from the next spawn on. */
  private selectLoadout(index: number): void {
    if (!this.conn) return;
    this.conn.send(
      create(ClientMessageSchema, { msg: { case: "selectLoadout", value: create(SelectLoadoutSchema, { index }) } }),
    );
    nextLoadout.value = index;
  }

  // ---- Settings ----

  /** Settings changed: frame limiter and interpolation mode follow immediately. */
  applySettings(): void {
    const s = settings.value;
    this.snapshots.setMode(s.network.interpolation);
    this.loop.setCap(frameCap(s.video.frameLimiter));
    this.hud.applySettings(s);
    this.renderer.applySettings(s.video);
  }
}
